import { useEffect, useState } from 'react'

/*
 * Lightweight access probe for the Video tab. Calls /Admin/Jitsi/access
 * once on mount + every refreshMs. Returns {loading, granted, enabled}.
 *
 * Used by the sidebar to decide whether the Video tab renders at all
 * and by individual Video/* pages to short-circuit a fetch loop when
 * access has been revoked mid-session.
 *
 * Poll is deduped module-wide: one poller per {feature}, no matter how
 * many components subscribe. The sidebar + open Video page + any hidden
 * Video tabs all share the same fetch, so /Admin/Jitsi/access is hit at
 * most once per refreshMs (per feature), instead of N times.
 *
 * Keeps the probe SUPERADMIN-friendly: same endpoint for every role,
 * so non-SUPER users never need to know about /Admin/Jitsi/health.
 */

// feature -> { state, subscribers, refreshMs, intervalId }
const cache = new Map()

function ensurePoller(feature, refreshMs) {
  let entry = cache.get(feature)
  if (entry) {
    // Tighten the interval if the new subscriber wants faster refresh.
    if (refreshMs < entry.refreshMs) {
      clearInterval(entry.intervalId)
      entry.refreshMs = refreshMs
      entry.intervalId = setInterval(entry.tick, refreshMs)
    }
    return entry
  }
  entry = {
    state: { loading: true, granted: false, enabled: false },
    subscribers: new Set(),
    refreshMs,
    intervalId: null,
    tick: null,
  }
  cache.set(feature, entry)
  entry.tick = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_IP}/Admin/Jitsi/access?feature=${encodeURIComponent(feature)}`,
        { credentials: 'include' }
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      entry.state = { loading: false, granted: !!data.granted, enabled: !!data.enabled }
    } catch {
      entry.state = { loading: false, granted: false, enabled: false }
    }
    entry.subscribers.forEach(cb => cb(entry.state))
  }
  entry.tick()
  entry.intervalId = setInterval(entry.tick, refreshMs)
  return entry
}

function subscribe(feature, refreshMs, cb) {
  const entry = ensurePoller(feature, refreshMs)
  entry.subscribers.add(cb)
  // Fire the callback with the current cached state so new subscribers
  // don't wait a full refreshMs to see the answer.
  cb(entry.state)
  return () => {
    entry.subscribers.delete(cb)
    if (entry.subscribers.size === 0) {
      clearInterval(entry.intervalId)
      cache.delete(feature)
    }
  }
}

export default function useJitsiAccess(feature = 'jitsi.video', refreshMs = 30000) {
  const [state, setState] = useState({ loading: true, granted: false, enabled: false })

  useEffect(() => {
    const unsub = subscribe(feature, Math.max(5000, refreshMs), setState)
    return unsub
  }, [feature, refreshMs])

  return state
}
